import { startTransition, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { syncClientSelectionState } from "../utils/resultsFleetData.js";
import { toggleClientSelection } from "../utils/resultsWorkspaceData.js";

function buildWorkspaceUiSnapshot(state, interactions, overrides = {}, buildFleetCards = null) {
  const assignment = overrides.assignment === undefined ? state?.currentAssignment || null : overrides.assignment || null;
  const trip = overrides.trip === undefined ? state?.currentTrip || null : overrides.trip || null;

  return {
    assignment,
    fleetCards: typeof buildFleetCards === "function"
      ? buildFleetCards(state, interactions)
      : [],
    selectedClientId: state?.selectedClientId || null,
    trip,
    workspaceRevision: Number(state?.workspaceRevision) || 0,
  };
}

function setWorkspaceSelectionState(state, assignment, trip, mapRenderMeta = null) {
  if (!state) {
    return;
  }

  const nextAssignmentId = assignment?.truck_id || null;
  const nextTripId = trip?.trip_id || null;
  const selectionEntry = state.selectedClientId
    ? state.clientEntriesById?.get?.(state.selectedClientId) || null
    : null;

  if (
    selectionEntry
    && (
      (nextAssignmentId && selectionEntry.assignmentId !== nextAssignmentId)
      || (nextTripId && selectionEntry.tripId !== nextTripId)
      || (!nextAssignmentId && !nextTripId)
    )
  ) {
    state.selectedClientId = null;
  }

  state.currentAssignment = assignment || null;
  state.currentTrip = trip || null;
  state.currentMapRenderMeta = mapRenderMeta || null;
  syncClientSelectionState(state);
}

export default function useResultsWorkspaceSelectionState({
  buildFleetCards = null,
  interactionsRef,
  workspaceStore,
}) {
  const mapRenderMetaKeyRef = useRef("null");

  const [workspaceUi, setWorkspaceUi] = useState({
    assignment: null,
    fleetCards: [],
    selectedClientId: null,
    trip: null,
  });
  const [selectionRequest, setSelectionRequest] = useState({
    centerMap: false,
    clientId: null,
    token: 0,
  });
  const [mapRenderMeta, setMapRenderMeta] = useState(null);

  const syncWorkspaceUi = ({ assignment = undefined, trip = undefined, synchronous = false } = {}) => {
    const state = workspaceStore.getState();

    const update = () => {
      setWorkspaceUi(
        state
          ? buildWorkspaceUiSnapshot(state, interactionsRef.current, { assignment, trip }, buildFleetCards)
          : {
              assignment: assignment === undefined ? null : assignment || null,
              fleetCards: [],
              selectedClientId: null,
              trip: trip === undefined ? null : trip || null,
            },
      );
    };
    // Truck refresh completion must include the DOM commit, not just queue it.
    if (synchronous) flushSync(update);
    else startTransition(update);
  };

  const renderAssignmentCards = () => {
    syncWorkspaceUi();
  };

  const resetMapRenderMeta = (state = workspaceStore.getState()) => {
    mapRenderMetaKeyRef.current = "null";
    if (state) {
      state.currentMapRenderMeta = null;
    }
    setMapRenderMeta(null);
  };

  const renderCurrentSelection = (assignment = undefined, trip = undefined, { synchronous = false } = {}) => {
    const state = workspaceStore.getState();
    if (!state) {
      return;
    }

    const nextAssignment = assignment === undefined ? state.currentAssignment : assignment;
    const nextTrip = trip === undefined ? state.currentTrip : trip;
    resetMapRenderMeta(state);

    if (!nextAssignment) {
      setWorkspaceSelectionState(state, null, null, null);
      syncWorkspaceUi({ assignment: null, trip: null, synchronous });
      return;
    }

    setWorkspaceSelectionState(state, nextAssignment, nextTrip, null);
    syncWorkspaceUi({ assignment: nextAssignment, trip: nextTrip, synchronous });
  };

  const handleMapRenderMetaChange = (nextMapRenderMeta = null) => {
    const state = workspaceStore.getState();
    if (!state) {
      return;
    }

    const nextKey = JSON.stringify(nextMapRenderMeta || null);
    if (mapRenderMetaKeyRef.current === nextKey) {
      return;
    }

    mapRenderMetaKeyRef.current = nextKey;
    state.currentMapRenderMeta = nextMapRenderMeta || null;
    setMapRenderMeta(nextMapRenderMeta || null);
  };

  const handleClientSelection = ({
    assignmentId = null,
    centerMap = false,
    clientId = null,
    openSelectionOnly = false,
    tripId = null,
  } = {}) => {
    const state = workspaceStore.getState();
    if (!state || !clientId) {
      return;
    }

    startTransition(() => {
      setSelectionRequest((currentRequest) => ({
        centerMap,
        clientId,
        token: currentRequest.token + 1,
      }));
    });

    const selection = toggleClientSelection(state, {
      assignmentId,
      clientId,
      openSelectionOnly,
      tripId,
    });

    if (selection.selectionChanged) {
      renderCurrentSelection(selection.nextAssignment, selection.nextTrip);
      return;
    }

    syncWorkspaceUi();
  };

  return {
    handleClientSelection,
    handleMapRenderMetaChange,
    mapRenderMeta,
    renderAssignmentCards,
    renderCurrentSelection,
    resetMapRenderMeta,
    selectionRequest,
    setWorkspaceSelectionState,
    syncWorkspaceUi,
    workspaceUi,
  };
}
